import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const ABOUT_IMAGE = "https://images.unsplash.com/photo-1519389950473-47ba0277781c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const CHAPTERS = [
  {
    num: "01",
    title: "We curate, you relax",
    text: "The internet has infinite products. We keep a few hundred. Every item in our catalogue survives a three-round review for utility, durability, and design — so you never scroll through noise.",
  },
  {
    num: "02",
    title: "Quality is non-negotiable",
    text: "We work directly with verified manufacturers, test samples in real Indian homes, and drop anything that doesn't hold up. If we wouldn't use it daily, we don't sell it.",
  },
  {
    num: "03",
    title: "Fair prices, honest service",
    text: "Premium shouldn't mean overpriced. Direct sourcing keeps our prices honest, and our support team — real humans, Mon to Sat — keeps shopping effortless.",
  },
];

const STATS = [
  { value: "15,000+", label: "Happy customers" },
  { value: "200+", label: "Curated products" },
  { value: "4.8/5", label: "Average rating" },
  { value: "28", label: "States served" },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8">
        <Reveal>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-neutral-400">Our Story</p>
          <h1 className="max-w-3xl font-heading text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl">
            We exist to make everyday life <span className="font-serif font-medium italic">a little easier.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-500 sm:text-base">
            Everyday Essentials started with a simple frustration: online shopping in India felt like a gamble. Endless
            listings, questionable quality, and prices that made no sense. We built the store we wished existed — small,
            considered, and genuinely useful.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="img-frame overflow-hidden rounded-2xl">
            <img src={ABOUT_IMAGE} alt="The Everyday Essentials studio" className="h-[320px] w-full object-cover sm:h-[440px]" loading="lazy" />
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <div className="grid grid-cols-1 gap-x-12 gap-y-12 lg:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.1}>
              <div className="border-t-2 border-neutral-900 pt-6">
                <span className="font-serif text-5xl italic leading-none text-neutral-200">{c.num}</span>
                <h2 className="mt-6 font-heading text-xl font-bold tracking-tight text-neutral-900">{c.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-neutral-500">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-neutral-200/70 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-14 sm:grid-cols-4 sm:px-6 lg:px-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06} className="text-center">
              <p className="font-heading text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-neutral-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 sm:py-24 lg:px-8">
        <Reveal>
          <h2 className="font-serif text-3xl font-medium text-neutral-900 sm:text-4xl">
            Come browse the <span className="italic">good stuff.</span>
          </h2>
          <Link
            to="/shop"
            data-testid="about-shop-cta"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neutral-900 px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-neutral-700"
          >
            Shop the Collection
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
